import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, User, LogOut, LayoutDashboard, Shield, Package, Gavel, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navLinks = [
  { to: "/browse", label: "Browse Auctions" },
  { to: "/how-it-works", label: "How It Works" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const isAuthed = user && typeof user === "object";
  const initials = isAuthed
    ? (user.name || user.email || "?").slice(0, 2).toUpperCase()
    : "?";

  return (
    <header className="sticky top-0 z-40 bg-black/70 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
            <div className="w-8 h-8 bg-orange-600 flex items-center justify-center border border-orange-500">
              <Gavel className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-black text-xl tracking-tighter uppercase text-white group-hover:text-orange-500 transition-colors">
              Storage<span className="text-orange-500">Vault</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.slice(1)}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-xs font-semibold uppercase tracking-[0.2em] transition-colors ${
                    isActive ? "text-orange-500" : "text-zinc-400 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {isAuthed ? (
              <>
              {user.is_seller ? (
                <Button
                  asChild
                  variant="outline"
                  className="rounded-none bg-transparent border-orange-500 hover:bg-orange-500/10 text-orange-500 text-xs uppercase tracking-widest"
                >
                  <Link to="/seller" data-testid="nav-seller-dashboard">
                    <Building2 className="w-4 h-4 mr-2" /> Seller Dashboard
                  </Link>
                </Button>
              ) : (
                <Button
                  asChild
                  variant="outline"
                  className="rounded-none bg-transparent border-zinc-700 hover:bg-zinc-900 hover:border-orange-500 text-xs uppercase tracking-widest"
                >
                  <Link to="/sell" data-testid="nav-sell-button">
                    <Package className="w-4 h-4 mr-2" /> Sell Unit
                  </Link>
                </Button>
              )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="nav-user-menu"
                      className="w-9 h-9 bg-zinc-900 border border-zinc-700 hover:border-orange-500 flex items-center justify-center text-xs font-bold uppercase tracking-widest text-white transition-colors"
                    >
                      {initials}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="rounded-none bg-zinc-950 border-zinc-800 min-w-[220px]"
                  >
                    <DropdownMenuLabel className="font-body text-xs uppercase tracking-widest text-zinc-500">
                      {user.email}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-zinc-800" />
                    <DropdownMenuItem
                      data-testid="menu-dashboard"
                      onClick={() => nav(user.is_seller ? "/seller" : "/dashboard")}
                      className="rounded-none cursor-pointer focus:bg-zinc-900 focus:text-orange-500"
                    >
                      {user.is_seller ? <Building2 className="w-4 h-4 mr-2" /> : <LayoutDashboard className="w-4 h-4 mr-2" />}
                      {user.is_seller ? "Seller Dashboard" : "My Dashboard"}
                    </DropdownMenuItem>
                    {!user.is_seller && (
                      <DropdownMenuItem
                        data-testid="menu-selling"
                        onClick={() => nav("/dashboard/selling")}
                        className="rounded-none cursor-pointer focus:bg-zinc-900 focus:text-orange-500"
                      >
                        <Package className="w-4 h-4 mr-2" /> My Listings
                      </DropdownMenuItem>
                    )}
                    {user.role === "admin" && (
                      <DropdownMenuItem
                        data-testid="menu-admin"
                        onClick={() => nav("/admin")}
                        className="rounded-none cursor-pointer focus:bg-zinc-900 focus:text-orange-500"
                      >
                        <Shield className="w-4 h-4 mr-2" /> Admin
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-zinc-800" />
                    <DropdownMenuItem
                      data-testid="menu-logout"
                      onClick={async () => { await logout(); nav("/"); }}
                      className="rounded-none cursor-pointer focus:bg-zinc-900 focus:text-red-500"
                    >
                      <LogOut className="w-4 h-4 mr-2" /> Log Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button
                  asChild
                  variant="ghost"
                  className="rounded-none text-xs uppercase tracking-widest hover:bg-zinc-900 hover:text-orange-500"
                >
                  <Link to="/login" data-testid="nav-login">Log In</Link>
                </Button>
                <Button
                  asChild
                  className="rounded-none bg-orange-600 border border-orange-500 hover:bg-orange-500 text-white text-xs uppercase tracking-widest"
                >
                  <Link to="/register" data-testid="nav-register">Sign Up</Link>
                </Button>
              </>
            )}
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-zinc-950 border-t border-zinc-800">
          <div className="px-4 py-3 flex flex-col gap-2">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="px-3 py-2 text-sm font-semibold uppercase tracking-widest text-zinc-300 hover:text-orange-500"
              >
                {l.label}
              </NavLink>
            ))}
            {isAuthed ? (
              <>
                <NavLink to="/dashboard" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold uppercase tracking-widest text-zinc-300">Dashboard</NavLink>
                <NavLink to="/sell" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold uppercase tracking-widest text-orange-500">Sell Unit</NavLink>
                {user.role === "admin" && (
                  <NavLink to="/admin" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold uppercase tracking-widest text-zinc-300">Admin</NavLink>
                )}
                <button onClick={async () => { setOpen(false); await logout(); nav("/"); }} className="text-left px-3 py-2 text-sm font-semibold uppercase tracking-widest text-red-500">Log Out</button>
              </>
            ) : (
              <>
                <NavLink to="/login" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold uppercase tracking-widest text-zinc-300">Log In</NavLink>
                <NavLink to="/register" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold uppercase tracking-widest text-orange-500">Sign Up</NavLink>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
